import * as React from 'react'
import { connect } from 'react-redux'
import { actions } from 'react-redux-form'

import { SRPRS } from 'components/_Assets/svg/Icon/srprs'
import { ActionList } from 'components/ActionList'
import Box, { BoxType } from 'components/Box'
import { Button, TYPE } from 'components/Button'
import { Checkbox, CheckboxType } from 'components/Checkboxes'
import { Grid, GridGap } from 'components/Grid'
import Heading from 'components/Heading'
import Hero from 'components/Hero'
import { HeroType } from 'components/Hero/Hero'
import { InfoBox, InfoBoxItem } from 'components/InfoBox/InfoBox'
import ListHeader from 'components/ListHeader'
import Section, { SectionType, Size } from 'components/Section/Section'
import Style from 'components/Style'
import { Timeline, TimelineItem } from 'components/Timeline'
import { EntryProps } from '../../data/entries/types'
import { City } from '../../data/models/funnelOrder/cities'
import { inControlOptions } from '../../data/models/funnelOrder/days'
import { Theme, ThemeTypes } from '../../data/models/funnelOrder/theme'
import { getSiteCountryICC2, SiteCountry } from '../../data/models/locale'
import { getClient } from '../../data/openapi'
import { AppState } from '../../data/state'
import { FunnelStep } from '../../data/ui'
import Calendar from '../../elements/Calendar'
import IfCountry from '../../elements/conditionals/IfCountry'
import FunnelFooter from '../../elements/footers/funnelFooter'
import FunnelBreadcrumbs from '../../elements/FunnelBreadcrumbs'
import Loader from '../../elements/Loader'
import GiftcardModal from '../../elements/modals/GiftcardModal'
import PersonModal from '../../elements/modals/PersonModal'
import { getNameWithTheme, getParamsWithTheme } from '../../routing/links'
import Airport from '../funnel/customise/options/airport'
import Days from '../funnel/customise/options/days'
import LockedAirport from '../funnel/customise/options/lockedAirport'
import LockedDestination from '../funnel/customise/options/lockedDestination'
import People from '../funnel/customise/options/people'
import RoomType from '../funnel/customise/options/roomType'
import { QueryKey } from '../funnel/customise/wrapper'
import {
  CustomiseWrapperInnerProps,
  withAirportsAndPrices,
} from '../funnel/customise/wrapperFunction'
import DisplayContent from '../shared/content'
import Page from '../shared/page_wrapper'
import BasicInfo from './components/basicInfo'
import HowItWorks from './components/entries/howItWorks'
import FAQ from './components/faq'
import FlavourBox from './components/flavourBox'

interface InControlProps extends EntryProps {
  total: number
  patternImage: string
  excludedCities: City[]
  country: SiteCountry
  setExcludedCities(cities: City[]): void
}

interface InControlState {
  loading: boolean
  cities: City[]
}

class InControlPage extends React.Component<
  InControlProps & CustomiseWrapperInnerProps,
  InControlState
> {
  private infoSection?: HTMLDivElement
  private detailsSection?: HTMLDivElement

  constructor(props: InControlProps & CustomiseWrapperInnerProps) {
    super(props)
    this.state = {
      loading: true,
      cities: [],
    }
  }

  public componentDidMount() {
    this.setCountries(true)
  }

  public componentDidUpdate(
    props: InControlProps & CustomiseWrapperInnerProps,
  ) {
    if (props.country !== this.props.country) {
      this.setCountries()
    }
  }

  public render() {
    const {
      entry,
      match,
      prices,
      loading,
      patternImage,
      excludedCities,
    } = this.props

    if (this.state.loading) {
      return <Loader />
    }

    if (entry.page === undefined) {
      throw new Error('No page properties')
    }

    const { airports } = this.props

    return (
      <Page
        match={match}
        page={entry.page.name}
        color={entry.colour}
        navBarType="funnel"
        navBarOverrideColour={
          entry.page.style ? entry.page.style.navbarColor : undefined
        }
        navFooter
      >
        <Hero
          blurMobile
          type={HeroType.flavourEntry}
          headingColor={
            entry.page.style ? entry.page.style.headingColor : undefined
          }
          headingText={
            <DisplayContent contentKey="title">{entry.name}</DisplayContent>
          }
          subHeading={<FunnelBreadcrumbs funnelStep={FunnelStep.Customise} />}
          src={entry.page.photos.src}
          src1024={entry.page.photos.src1024}
          src1280={entry.page.photos.src1280}
          src1600={entry.page.photos.src1600}
          src1920={entry.page.photos.src1920}
        >
          <FunnelBreadcrumbs funnelStep={FunnelStep.Customise} />
          <Heading level={1}>
            <DisplayContent contentKey="title">{entry.name}</DisplayContent>
          </Heading>
          <InfoBox>
            <InfoBoxItem
              icon={SRPRS.calendarColored()}
              firstLine={
                <DisplayContent contentKey="1-info-top">3 - 14</DisplayContent>
              }
              secondLine={
                <DisplayContent contentKey="1-info-bottom">
                  Dagen
                </DisplayContent>
              }
            />
            <InfoBoxItem
              icon={
                entry.theme === Theme.fly
                  ? SRPRS.aeroplaneColored
                  : SRPRS.carColored
              }
              firstLine={
                <DisplayContent contentKey="2-info-top">2 tot 4</DisplayContent>
              }
              secondLine={
                <DisplayContent contentKey="2-info-bottom">
                  Steden
                </DisplayContent>
              }
            />
            <InfoBoxItem
              icon={SRPRS.mapMarkerColored}
              iconProps={{
                width: '37',
                height: '37',
                viewBox: '0 0 37 41',
                fill: 'none',
              }}
              firstLine={
                <DisplayContent contentKey="3-info-top">Heel</DisplayContent>
              }
              secondLine={
                <DisplayContent contentKey="3-info-bottom">
                  Europa
                </DisplayContent>
              }
            />
          </InfoBox>
        </Hero>
        <BasicInfo
          detailsScroll={this.handleDetailsScroll}
          infoScroll={this.handleInfoScroll}
        />
        <Section type={SectionType.solid} size={Size.s}>
          <Style margin={[[0, 0, 20, 0]]}>
            <div ref={this.setDetailsRef} />
            <Timeline direction="vertical" numbers>
              <TimelineItem>
                <Heading level={3}>
                  <DisplayContent contentKey="exclude-cities-heading">
                    Which cities will it be?
                  </DisplayContent>
                </Heading>
                <p>
                  <DisplayContent contentKey="exclude-cities-subheading">
                    Do you just strike 2 out of contention of all 5? You decide.
                  </DisplayContent>
                </p>
                <Grid nrCols={[2, 3]} colGap={[GridGap.m]} rowGap={[GridGap.m]}>
                  {this.state.cities.map(city => {
                    return (
                      <Checkbox
                        name={`exclude-${city.id}`}
                        checkboxType={CheckboxType.Box}
                        key={city.id}
                        value={city.id}
                        disabled={
                          excludedCities.length >= 5 &&
                          !this.cityIncluded(excludedCities, city)
                        }
                        checked={!this.cityIncluded(excludedCities, city)}
                        onChange={this.handleChange}
                      >
                        <DisplayContent
                          addNode
                          edit={true}
                          contentKey={`choice-${city.name}`}
                        >
                          {city.name}
                        </DisplayContent>
                      </Checkbox>
                    )
                  })}
                </Grid>
              </TimelineItem>
              <TimelineItem>
                <ActionList>
                  <People />
                  <Days options={inControlOptions} />
                  <RoomType />
                  <Airport {...{ airports, noTimes: true }} />
                  <LockedDestination />
                </ActionList>
              </TimelineItem>
              <TimelineItem>
                <Box fullWidthMobile>
                  <Calendar
                    actionList
                    hideTrigger
                    showCheapest={true}
                    prices={prices}
                    loading={loading}
                  />
                </Box>
              </TimelineItem>
            </Timeline>
          </Style>
          <PersonModal />
        </Section>
        <FunnelFooter
          nextStepNo={2}
          nextStepLink={getNameWithTheme('extras')}
          nextStepLinkParams={getParamsWithTheme()}
          nextStepName={
            <DisplayContent edit={true} contentKey="extras">
              Extras
            </DisplayContent>
          }
          stickyStop
          nextStepDisabled={isNaN(this.props.total)}
        />
        <GiftcardModal />
        <FlavourBox
          patternImage={patternImage}
          setInfoRef={this.setInfoRef}
          content={entry.page.content}
        />
        <HowItWorks boxTypeColor={BoxType.paleTurquoise} />
        <Section type={SectionType.white}>
          <ListHeader noMarginBottom>
            <Button
              cutOff="none"
              type={TYPE.plain}
              onClick={this.handleDetailsScroll}
            >
              <DisplayContent contentKey="boek-direct" edit={true}>
                Boek direct
              </DisplayContent>
            </Button>
          </ListHeader>
        </Section>
        <FAQ />
      </Page>
    )
  }

  private setCountries = (mount?: boolean) => {
    getClient()
      .then(client =>
        client.apis.bookings.bookings_theme_cities({
          theme: 'in-control',
          type: ThemeTypes.fly,
          site_country: getSiteCountryICC2(this.props.country),
        }),
      )
      .then(response => response.body)
      .then(cities => {
        this.setState({ cities, loading: false }, () => {
          if (mount) {
            const hash = window.location.hash
            if (hash !== undefined && hash === '#steden') {
              setTimeout(() => {
                if (this.detailsSection) {
                  this.detailsSection.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                  })
                }
              }, 2000)
            } else {
              window.scrollTo(0, 0)
            }
          }
        })
      })
  }

  private setInfoRef = (ref: HTMLDivElement) => {
    this.infoSection = ref
  }

  private setDetailsRef = (ref: HTMLDivElement) => {
    this.detailsSection = ref
  }

  private handleInfoScroll = () => {
    if (this.infoSection) {
      this.infoSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  private handleDetailsScroll = () => {
    if (this.detailsSection) {
      this.detailsSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  private cityIncluded = (cities: City[], city: City) => {
    return cities.map(x => x.id).includes(city.id)
  }

  private handleChange = (value: any, checked: boolean) => {
    const cities = [...this.props.excludedCities]
    const filteredCities = this.state.cities.filter(x => x.id === Number(value))
    const city = filteredCities.length > 0 ? filteredCities[0] : null

    if (city !== null) {
      if (this.cityIncluded(cities, city) && checked) {
        const index = cities.map(x => x.id).indexOf(city.id)
        cities.splice(index, 1)
      }
      if (!this.cityIncluded(cities, city) && !checked) {
        cities.push(city)
      }

      this.props.setExcludedCities(cities)
    }
  }
}

const ConnectedInControlPage = connect(
  (state: AppState) => ({
    patternImage: state.page.patternImage,
    total: state.funnelOrder.total,
    excludedCities: state.funnelOrder.excludedCitiesIds,
    country: state.locale.country,
  }),
  dispatch => ({
    setExcludedCities: (cities: City[]) => {
      dispatch(actions.change('funnelOrder.excludedCitiesIds', cities))
    },
  }),
)(InControlPage)

export default withAirportsAndPrices<EntryProps>(ConnectedInControlPage, [
  QueryKey.month,
  QueryKey.year,
])
